import { dominosStore } from './../../main';
import { action, IReactionDisposer, observable, makeObservable } from 'mobx';
import FormDeliveryAddress from './FormDeliveryAddress';
import { initMap, selectMarker } from '../MapAllPlaces';
import { oneTrustIsInitialized } from '../../shared/utils/oneTrust/isInitialized';
import { subscribeToConsentChanged } from '../../shared/utils/oneTrust/subscribeToConsentChanged';
import { getOneTrustConsentStatus } from '../../shared/utils/oneTrust/getStatusData';
import { GroupIdType } from '../../types/oneTrust';
import { CLASS_IS_NOSCROLL } from '../../shared/global/base';
import { ConsentManager } from '../../shared/utils/oneTrust/consentManager';

export class ChangePopupStore {
    @observable step: ChangePopupStepsType;
    @observable deliveryType: DeliveryType;
    @observable address: AddressData;
    @observable step2Error: string;

    wrapper: HTMLElement;
    config: ChangePopupConfigType;
    dictionary: ChangePopupDictionary;
    refs: ChangePopupRefsType;
    form: FormDeliveryAddress;

    maploaded = false;
    mappopupmode: boolean;
    mapid: number;
    onlyMapOnOverlay: boolean;

    local: LocalMapData;
    distance: number;

    disposers: IReactionDisposer[] = [];

    constructor(wrapper: HTMLElement, config: ChangePopupConfigType) {
        makeObservable(this);
        this.wrapper = wrapper;
        // Get initial config & dictionary
        this.config = config;
        this.dictionary = this.getConfig(wrapper);
        // Get all refs
        this.refs = this.setupRefs(wrapper);
        // Map in 'mobile' mode
        this.mappopupmode = !dominosStore.isMBreakpoint;
        // onlyMapOnOverlay means that choosing of the receive type is just on page, on overlay is map only.
        this.onlyMapOnOverlay = wrapper.hasAttribute('data-only-map-on-overlay');
        const options: FormOptions = { id: 'popupchangeplace' };
        this.form = new FormDeliveryAddress(this.refs.form, options, this);
        this.form.init();
    }

    // Setup functions

    /**
     * Dictionary for the whole popup
     * @param wrapper
     */
    getConfig(wrapper: HTMLElement) {
        try {
            const configScript = wrapper.querySelector('[data-popupchangeplace-config]');
            return JSON.parse(configScript.innerHTML);
        } catch (e) {
            return {};
        }
    }

    /**
     * References for all elements within popup that has to be updated after changes
     * @param wrapper
     */
    setupRefs(wrapper: HTMLElement): ChangePopupRefsType {
        const sections: NodeListOf<HTMLElement> = wrapper.querySelectorAll(
            '[data-popupchangeplace-section]'
        );
        const btns: NodeListOf<HTMLButtonElement> = wrapper.querySelectorAll(
            '[data-popupchangeplace-btn]'
        );

        return {
            title: wrapper.querySelector('[data-popupchangeplace-title]'),
            nav: wrapper.querySelector('[data-popupchangeplace-nav]'),
            message: wrapper.querySelector('[data-popupchangeplace-message]'),
            btns: Array.from(btns).map(btn => ({
                type: parseInt(btn.getAttribute('data-popupchangeplace-btn')),
                ref: btn
            })),
            line: wrapper.querySelector('[data-popupchangeplace-line]'),
            steps: Array.from(wrapper.querySelectorAll('[data-popupchangeplace-step]')),
            // If there is only-map-on-overlay variant then the container for map will be created after overlay initialization.
            map:  wrapper.hasAttribute('data-only-map-on-overlay') ? null : wrapper.querySelector('[data-popupchangeplace-map]'),
            sections: Array.from(sections).map(section => ({
                type: parseInt(section.getAttribute('data-popupchangeplace-section')),
                ref: section
            })),
            form: wrapper.querySelector('[data-popupchangeplace-form]'),
            formtext: wrapper.querySelector('[data-popupchangeplace-formtext]'),

            localinfo: {
                header: wrapper.querySelector('[data-popupchangeplace-localinfo-header]'),
                city: wrapper.querySelector('[data-popupchangeplace-localinfo-city]'),
                address: wrapper.querySelector('[data-popupchangeplace-localinfo-address]'),
                pickupencourage: wrapper.querySelector(
                    '[data-popupchangeplace-localinfo-pickupencourage]'
                ),
                pintitle: wrapper.querySelector('[data-popupchangeplace-localinfo-pintitle]'),
                pinprefix: wrapper.querySelector('[data-popupchangeplace-localinfo-pinprefix]'),
                pinname: wrapper.querySelector('[data-popupchangeplace-localinfo-pinname]'),
                timetitle: wrapper.querySelector('[data-popupchangeplace-localinfo-timetitle]'),
                timeerror: wrapper.querySelector('[data-popupchangeplace-localinfo-timeerror]'),
                timevalue: wrapper.querySelector('[data-popupchangeplace-localinfo-timevalue]'),
                timeinfo: wrapper.querySelector('[data-popupchangeplace-localinfo-timeinfo]'),
                btnaccept: wrapper.querySelector('[data-popupchangeplace-localinfo-btnaccept]'),
                btndeny: wrapper.querySelector('[data-popupchangeplace-localinfo-btndeny]'),
                responsestep2: wrapper.querySelector('[data-formpopupchangeplace-responsestep2]')
            }
        };
    }

    // Computed values

    // Action functions

    /**
     * Available steps:
     * 1: form & map
     * 2: local info
     * 3: mobile map view
     *
     * @param {number} step
     * @memberof ChangePopupStore
     */
    @action changeStep = (step: ChangePopupStepsType) => {
        this.step = step;
    };

    /**
     *
     *
     * @param {AddressData} address
     * @memberof ChangePopupStore
     */
    @action saveAddress = (address: AddressData) => {
        this.address = { ...address };
    };

    @action chandeDeliveryType = (delivery: DeliveryType) => {
        this.deliveryType = delivery;
    };

    /**
     * When distance is 0 or undefined local is found by API and ready for delivery.
     * Otherwise local is found by Google and is considered as 'near'
     *
     * @param {LocalMapData} local
     * @param {number} distance
     * @memberof ChangePopupStore
     */
    @action changeLocal = (local: LocalMapData, distance: number) => {
        this.local = local;
        this.distance = distance;

        if (this.deliveryType === 1 || this.mappopupmode || this.onlyMapOnOverlay || !getOneTrustConsentStatus(GroupIdType.Functional)) {
            // Go to summup
            this.changeStep(2);
        } else {
            //
            selectMarker(local.Location.Lat, local.Location.Lng, this.mapid);
        }
    };

    // Utils functions

    /**
     * Should be fired after closing popup
     *
     * @memberof ChangePopupStore
     */
    fireDisposers = () => {
        this.disposers.map(dispose => dispose());
    };

    @action initMap = (popupmode: boolean, mapContainerAttribute? : string) => {
        this.mappopupmode = popupmode;
        // If no this.refs.map it means that map container was created after refs creating,
        // therefore we are looking for a map container now.
        const mapContainer = this.refs.map || document.querySelector(`[${mapContainerAttribute}]`);
        const runMap = ()=>{
            this.maploaded = true;
            this.mapid = Date.now();
            initMap(
                mapContainer,
                () => this.mappopupmode,
                this.mapid,
                this.config.oncomplete === 'void' ? this : null
            );
        }

        if (!this.maploaded && getOneTrustConsentStatus(GroupIdType.Functional)) {
            runMap();
        } else {
            mapContainer.classList.add('m-MapAllPlaces__cookie-not-accepted');
            oneTrustIsInitialized() &&
                mapContainer.addEventListener('click', window.OneTrust.ToggleInfoDisplay);
            ConsentManager.addCallback(e => {
                // check if the overlay is still open
                const overlayIsOpened = document.body.classList.contains(CLASS_IS_NOSCROLL);
                if (e.detail?.includes(GroupIdType.Functional) && overlayIsOpened) {
                    mapContainer.removeEventListener('click', window.OneTrust.ToggleInfoDisplay);
                    mapContainer.classList.remove('m-MapAllPlaces__cookie-not-accepted');
                    runMap();
                }
            });
            subscribeToConsentChanged();
        }
    };

    @action setStep2Error = (message: string) => {
        this.step2Error = message;
    };
}
